/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { TextInput } from '@/shared/ui/components/form/inputs';
import { cn } from '@/shared/ui/utils/tailwind';
import type {
  FieldValueSuggestion,
  PropertySuggestableField,
} from '@services/requests/generated/types.gen';
import { isSearchableQuery, useFieldValueSuggestions } from './useFieldValueSuggestions';

export interface FieldValueAutocompleteProps {
  agencyId: string;
  field: PropertySuggestableField;
  label: string;
  value: string | undefined;
  /** Every keystroke: the field stays free text. */
  onChange: (value: string) => void;
  /** A value picked from the list (not typed), e.g. to prefill from it. */
  onPick?: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  error?: string;
  required?: boolean;
  'data-field'?: string;
}

/**
 * The context line of one suggestion: "12 imóveis · Indaiá · Construtora X".
 *
 * @param item - The suggestion.
 * @param countLabel - The translated count ("12 imóveis").
 * @returns The joined context.
 */
export function suggestionContextLine(item: FieldValueSuggestion, countLabel: string): string {
  const context = Object.values(item.context ?? {}).filter((v) => typeof v === 'string' && v.trim() !== '');
  return [countLabel, ...context].join(' · ');
}

const optionClass =
  'flex w-full flex-col items-start gap-[var(--space-0-5)] px-[var(--space-3)] py-[var(--space-2)] text-left';

/** What a key press does to the open list. */
export type ListKeyAction =
  | { type: 'move'; highlight: number }
  | { type: 'pick'; index: number }
  | { type: 'keep' }
  | { type: 'none' };

/**
 * Next highlighted row for an arrow key, wrapping around the list.
 *
 * @param key - 'ArrowDown' or 'ArrowUp'.
 * @param current - The highlighted row, -1 when none.
 * @param total - Rows in the list, the "register as new" row included.
 * @returns The new highlighted row.
 */
function moveHighlight(key: string, current: number, total: number): number {
  if (key === 'ArrowDown') return (current + 1) % total;
  return current <= 0 ? total - 1 : current - 1;
}

/**
 * Enter on the highlighted row: a value is picked, the last row keeps the text.
 *
 * @param highlight - The highlighted row.
 * @param itemCount - Suggested values in the list.
 * @returns The action, none when no row is highlighted.
 */
function enterAction(highlight: number, itemCount: number): ListKeyAction {
  if (highlight < 0) return { type: 'none' };
  return highlight < itemCount ? { type: 'pick', index: highlight } : { type: 'keep' };
}

/**
 * Maps a key press on the open list to what it does.
 *
 * @param key - The pressed key.
 * @param highlight - The highlighted row, -1 when none.
 * @param itemCount - Suggested values in the list (the "register" row comes after).
 * @returns The action to apply.
 */
export function listKeyAction(key: string, highlight: number, itemCount: number): ListKeyAction {
  if (key === 'ArrowDown' || key === 'ArrowUp') {
    return { type: 'move', highlight: moveHighlight(key, highlight, itemCount + 1) };
  }
  if (key === 'Enter') return enterAction(highlight, itemCount);
  return key === 'Escape' ? { type: 'keep' } : { type: 'none' };
}

interface SuggestionListProps {
  field: PropertySuggestableField;
  label: string;
  text: string;
  items: FieldValueSuggestion[];
  loading: boolean;
  highlight: number;
  onPick: (value: string) => void;
  onKeep: () => void;
}

/**
 * One suggested value: the value, then its count and context.
 *
 * @param props - The suggestion, whether it is highlighted, and the pick handler.
 * @returns The option row.
 */
function SuggestionOption({ item, active, onPick }: { item: FieldValueSuggestion; active: boolean; onPick: (v: string) => void }) {
  const { t } = useTranslation('features/properties');
  return (
    <UnstyledButton
      type="button"
      role="option"
      aria-selected={active}
      onMouseDown={(e) => { e.preventDefault(); onPick(item.value); }}
      className={cn(optionClass, 'border-b border-[var(--border-ultra-subtle)] hover:bg-[var(--surface-hover)]', active && 'bg-[var(--surface-hover)]')}
    >
      <Text variant="bodySm" as="span" className="break-words [font-weight:var(--font-semibold)] text-[var(--text-primary)]">
        {item.value}
      </Text>
      <Text variant="meta" as="span" className="break-words text-[var(--text-tertiary)]">
        {suggestionContextLine(item, t('propertyDrawer.autocomplete.count', { count: item.count }))}
      </Text>
    </UnstyledButton>
  );
}

/**
 * The open list: the agency's values, a searching line while the first
 * answer is on its way, and the row that keeps the typed text as new.
 *
 * @param props - What to list and the handlers.
 * @returns The list box.
 */
function SuggestionList({ field, label, text, items, loading, highlight, onPick, onKeep }: SuggestionListProps) {
  const { t } = useTranslation('features/properties');
  const keepActive = highlight === items.length;
  return (
    <div
      role="listbox"
      aria-label={label}
      data-testid={`field-values-${field}`}
      className="absolute left-0 right-0 top-full z-50 mt-[var(--space-1)] max-h-[260px] overflow-y-auto rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-[var(--shadow-lg)]"
    >
      {items.map((item, index) => (
        <SuggestionOption key={item.value} item={item} active={highlight === index} onPick={onPick} />
      ))}
      {loading && items.length === 0 && (
        <Text variant="meta" as="span" className="block px-[var(--space-3)] py-[var(--space-2)] text-[var(--text-tertiary)]">
          {t('propertyDrawer.autocomplete.loading')}
        </Text>
      )}
      <UnstyledButton
        type="button"
        role="option"
        aria-selected={keepActive}
        onMouseDown={(e) => { e.preventDefault(); onKeep(); }}
        className={cn(optionClass, 'hover:bg-[var(--surface-hover)]', keepActive && 'bg-[var(--surface-hover)]')}
      >
        <Text variant="bodySm" as="span" className="break-words [font-weight:var(--font-semibold)] text-[var(--seed-base)]">
          {t('propertyDrawer.autocomplete.registerNew', { value: text.trim() })}
        </Text>
      </UnstyledButton>
    </div>
  );
}

/**
 * Open/highlight state of the list and the handlers that drive it.
 *
 * @param props - The autocomplete props.
 * @returns State, suggestions and handlers for the input and the list.
 */
function useAutocompleteList(props: FieldValueAutocompleteProps) {
  const { agencyId, field, value, onChange, onPick } = props;
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const text = value ?? '';
  const { items, loading } = useFieldValueSuggestions(agencyId, field, text, open);
  const showList = open && isSearchableQuery(text);

  const close = () => { setOpen(false); setHighlight(-1); };
  const pick = (picked: string) => { onChange(picked); onPick?.(picked); close(); };
  const type = (next: string) => { onChange(next); setOpen(true); setHighlight(-1); };

  const apply: Record<ListKeyAction['type'], (action: ListKeyAction) => void> = {
    move: (action) => setHighlight((action as { highlight: number }).highlight),
    pick: (action) => pick(items[(action as { index: number }).index].value),
    keep: () => close(),
    none: () => undefined,
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const action = showList ? listKeyAction(event.key, highlight, items.length) : { type: 'none' as const };
    if (action.type === 'none') return;
    event.preventDefault();
    apply[action.type](action);
  };

  return { text, items, loading, highlight, showList, setOpen, pick, close, type, onKeyDown };
}

/**
 * Free-text input that suggests the values the agency already uses for the
 * field (plan §4): two letters, a 250 ms pause, then the agency's values
 * that contain the text, most used first, each with its count and context.
 * The last row keeps the typed text as a new value; nothing is created.
 *
 * @param props - Field identity, value and handlers.
 * @returns The input with its suggestion list.
 */
export function FieldValueAutocomplete(props: FieldValueAutocompleteProps) {
  const { field, label, placeholder, maxLength, error, required } = props;
  const list = useAutocompleteList(props);

  return (
    <div className="relative w-full">
      <TextInput
        label={label}
        required={required}
        value={list.text}
        error={error}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete="off"
        role="combobox"
        aria-expanded={list.showList}
        aria-autocomplete="list"
        data-field={props['data-field']}
        onChange={(e) => list.type(e.target.value)}
        onFocus={() => list.setOpen(true)}
        onBlur={() => list.setOpen(false)}
        onKeyDown={list.onKeyDown}
      />
      {list.showList && (
        <SuggestionList
          field={field}
          label={label}
          text={list.text}
          items={list.items}
          loading={list.loading}
          highlight={list.highlight}
          onPick={list.pick}
          onKeep={list.close}
        />
      )}
    </div>
  );
}
